import React, { useEffect, useState, useRef, Component } from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Scale, Search, BrainCircuit, Workflow, ShieldCheck, PenTool, ArrowRight, FileText, Clock, CheckSquare, CheckCircle2 } from 'lucide-react';
// --- Animation Hooks & Components ---
function useInView(options?: IntersectionObserverInit) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.unobserve(el); // Only animate once
      }
    }, {
      threshold: 0.1,
      ...options
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [options]);
  return [ref, isInView] as const;
}
function AnimatedSection({
  children,
  className = '',
  animation = 'animate-fade-in-up',
  delay = ''
}: {
  children: React.ReactNode;
  className?: string;
  animation?: string;
  delay?: string;
}) {
  const [ref, isInView] = useInView();
  return <div ref={ref} className={`${className} ${isInView ? animation : 'opacity-0-initial'} ${delay}`} data-id="element-1117">
      {children}
    </div>;
}
// ------------------------------------
interface HomepageProps {
  onNavigate: (page: string) => void;
}
export function Homepage({
  onNavigate
}: HomepageProps) {
  return <div className="min-h-screen bg-background text-foreground selection:bg-blue-100 selection:text-blue-900" data-id="element-1118">
      {/* Navigation Bar */}
      <nav className="sticky top-0 z-50 w-full border-b border-white/10 bg-black/95 backdrop-blur supports-[backdrop-filter]:bg-black/80" data-id="element-1119">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between" data-id="element-1120">
          <div className="flex items-center" data-id="element-1121">
            <img src="https://cdn.magicpatterns.com/uploads/xaM7Qq1sxDCqJoWJci6P4r/EF-logo-white.png" alt="Festeryga Law" className="h-8 w-auto" data-id="element-1122" />
          </div>
          <div className="flex items-center" data-id="element-1123">
            <Button size="default" className="uppercase tracking-wider text-xs font-bold bg-red-700 hover:bg-red-600 text-white shadow-md shadow-red-900/30 hover:shadow-lg hover:shadow-red-900/40 hover:-translate-y-0.5 transition-all duration-200 px-6" onClick={() => onNavigate('login')} data-id="element-1124">
              Enter Platform Demo
              <ArrowRight className="ml-2 w-4 h-4" data-id="element-1125" />
            </Button>
          </div>
        </div>
      </nav>

      <main data-id="element-1126">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-black text-white pt-24 pb-32" data-id="element-1127">
          <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1128">
            <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-red-900/20 blur-[120px] animate-float" data-id="element-1129" />
            <div className="absolute top-[60%] -right-[10%] w-[40%] h-[40%] rounded-full bg-slate-800/40 blur-[100px] animate-float-delayed" data-id="element-1130" />
            {/* Subtle grid pattern */}
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L3N2Zz4=')] opacity-20 mask-image:linear-gradient(to_bottom,white,transparent)" data-id="element-1131" />
          </div>

          <div className="container mx-auto px-4 relative z-10" data-id="element-1132">
            <div className="max-w-4xl mx-auto text-center space-y-8" data-id="element-1133">
              <AnimatedSection data-id="element-1134">
                <Badge variant="outline" className="border-red-700/30 text-red-300 bg-red-700/10 px-4 py-1.5 text-sm animate-pulse-glow" data-id="element-1135">
                  Internal Stakeholder Preview
                </Badge>
              </AnimatedSection>

              <AnimatedSection delay="delay-100" data-id="element-1136">
                <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-tight font-heading" data-id="element-1137">
                  Festeryga Law's secure, local-first legal intelligence
                  platform.
                </h1>
                <p className="text-red-400 italic text-lg md:text-xl mt-4 font-medium" data-id="element-1138">
                  When It's Everything™
                </p>
              </AnimatedSection>

              <AnimatedSection delay="delay-200" data-id="element-1139">
                <p className="text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed" data-id="element-1140">
                  Purpose-built for Festeryga Law's plaintiff injury practice.
                  Search your entire corpus, automate high-friction workflows,
                  and draft with evidence — all on your own infrastructure.
                </p>
              </AnimatedSection>

              <AnimatedSection delay="delay-300" data-id="element-1141">
                <div className="flex flex-col sm:flex-row items-center justify-center gap-5 pt-8" data-id="element-1142">
                  <Button size="lg" className="bg-red-700 hover:bg-red-600 text-white w-full sm:w-auto text-lg font-bold h-16 px-12 shadow-xl shadow-red-700/40 hover:shadow-2xl hover:shadow-red-700/50 transition-all duration-200 rounded-xl ring-1 ring-red-700/30 group uppercase tracking-wider" onClick={() => onNavigate('login')} data-id="element-1143">
                    Enter Platform Demo{' '}
                    <ArrowRight className="ml-2.5 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-1144" />
                  </Button>
                  <Button size="lg" variant="outline" className="border-2 border-slate-400 text-white hover:bg-white/10 hover:border-white w-full sm:w-auto text-lg font-semibold h-16 px-12 transition-all duration-200 rounded-xl backdrop-blur-sm uppercase tracking-wider" onClick={() => {
                  document.getElementById('roadmap')?.scrollIntoView({
                    behavior: 'smooth'
                  });
                }} data-id="element-1145">
                    <FileText className="mr-2.5 w-5 h-5" data-id="element-1146" />
                    View Architecture Specs
                  </Button>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Value Pillars */}
        <section className="py-24 bg-muted" data-id="element-1147">
          <div className="container mx-auto px-4" data-id="element-1148">
            <AnimatedSection className="text-center mb-16" data-id="element-1149">
              <h2 className="text-3xl font-bold tracking-tight text-foreground mb-4 font-heading" data-id="element-1150">
                Built for How Festeryga Law Works
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto" data-id="element-1151">
                Every capability is designed around your plaintiff injury
                caseload — from intake through settlement. AI assists, but your
                attorneys always decide.
              </p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-1152">
              {[{
              icon: Search,
              title: 'Grounded Legal Search',
              description: "Query Festeryga Law's entire document corpus using natural language. Get synthesized answers with explicit Bates-stamped citations back to your source records.",
              delay: ''
            }, {
              icon: BrainCircuit,
              title: 'Matter-Aware Intelligence',
              description: 'Each matter maintains its own isolated context. The system automatically extracts key entities, treatment dates, and provider relationships from incoming medical records and discovery.',
              delay: 'delay-100'
            }, {
              icon: Workflow,
              title: 'Workflow Automation',
              description: 'Standardize the tasks your team runs every week — Bates labeling, medical chronology generation, production classification, and email-to-matter filing — with built-in review controls.',
              delay: 'delay-200'
            }, {
              icon: ShieldCheck,
              title: 'Local-First, Air-Gapped',
              description: "Zero cloud dependency. All of Festeryga Law's client data stays on your own secure infrastructure. No data leaves the firm network — ever.",
              delay: 'delay-300'
            }, {
              icon: PenTool,
              title: 'Drafting Copilot',
              description: "Generate first-draft demand letters, petitions, and discovery responses grounded strictly in your matter's approved evidence pack. Every claim is citation-backed.",
              delay: 'delay-400'
            }, {
              icon: CheckSquare,
              title: 'Your attorneys decide. Always.',
              description: "Festeryga Law's platform is built on the principle that AI assists, but attorneys decide. Every automated action passes through a structured review queue, backed by an immutable audit trail your team can inspect at any time.",
              delay: 'delay-500'
            }].map((pillar, index) => {
              const Icon = pillar.icon;
              return <AnimatedSection key={index} delay={pillar.delay} data-id="element-1153">
                    <Card className="border-none shadow-sm bg-card hover:shadow-md transition-shadow h-full hover:-translate-y-1 duration-300" data-id="element-1154">
                      <CardContent className="p-6" data-id="element-1155">
                        <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" data-id="element-1156">
                          <Icon className="w-6 h-6 text-red-700" data-id="element-1157" />
                        </div>
                        <h3 className="text-xl font-semibold text-foreground mb-2" data-id="element-1158">
                          {pillar.title}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed" data-id="element-1159">
                          {pillar.description}
                        </p>
                      </CardContent>
                    </Card>
                  </AnimatedSection>;
            })}
            </div>
          </div>
        </section>

        {/* Feature Previews */}
        <section className="py-24 border-t overflow-hidden" data-id="element-1160">
          <div className="container mx-auto px-4 space-y-32" data-id="element-1161">
            {/* Preview 1: Grounded Search */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto" data-id="element-1162">
              <AnimatedSection animation="animate-fade-in-left" className="space-y-6" data-id="element-1163">
                <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-red-50 text-red-700 border-red-200" data-id="element-1164">
                  Knowledge Retrieval
                </div>
                <h2 className="text-3xl font-bold tracking-tight text-foreground font-heading" data-id="element-1165">
                  Answers backed by your evidence.
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed" data-id="element-1166">
                  Stop hunting through Dropbox folders and local drives. Ask
                  complex questions about any Festeryga Law matter and receive
                  synthesized answers with direct citations to the exact page
                  and Bates number.
                </p>
                <ul className="space-y-3" data-id="element-1167">
                  {['Semantic search across all document types', 'Explicit Bates-stamped citations', 'Confidence scoring for reliability'].map((item, i) => <li key={i} className="flex items-center text-sm font-medium text-foreground" data-id="element-1168">
                      <CheckCircle2 className="w-4 h-4 mr-3 text-red-700" data-id="element-1169" />{' '}
                      {item}
                    </li>)}
                </ul>
              </AnimatedSection>
              <AnimatedSection animation="animate-fade-in-right" className="relative" data-id="element-1170">
                <div className="absolute -inset-4 bg-red-50/50 rounded-2xl transform rotate-2 animate-pulse-glow" data-id="element-1171"></div>
                <Card className="relative shadow-xl border-border bg-card hover:shadow-2xl transition-shadow duration-500" data-id="element-1172">
                  <CardHeader className="border-b bg-muted/50 pb-4" data-id="element-1173">
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-2" data-id="element-1174">
                      <Search className="w-4 h-4 text-red-700/90" data-id="element-1175" />
                      <span className="typing-animation" data-id="element-1176">
                        Query: Evidence of driver fatigue
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6 space-y-4" data-id="element-1177">
                    <div className="flex items-center space-x-2 mb-2" data-id="element-1178">
                      <BrainCircuit className="w-5 h-5 text-red-700" data-id="element-1179" />
                      <h3 className="font-semibold text-sm text-foreground" data-id="element-1180">
                        Synthesized Answer
                      </h3>
                      <Badge variant="outline" className="ml-auto text-[10px] bg-green-50 text-green-700 border-green-200" data-id="element-1181">
                        High Confidence
                      </Badge>
                    </div>
                    <p className="text-sm text-foreground/80 leading-relaxed" data-id="element-1182">
                      The Defendant Driver ELD Logs{' '}
                      <sup className="text-red-700 font-bold hover:bg-red-100 px-1 rounded cursor-pointer transition-colors" data-id="element-1183">
                        [1]
                      </sup>{' '}
                      indicate James Whitfield had been driving for 13.5 hours
                      prior to the collision. The Police Report{' '}
                      <sup className="text-red-700 font-bold hover:bg-red-100 px-1 rounded cursor-pointer transition-colors" data-id="element-1184">
                        [2]
                      </sup>{' '}
                      notes the driver stated he was "exhausted".
                    </p>
                    <div className="mt-4 pt-4 border-t space-y-2" data-id="element-1185">
                      <div className="text-xs font-medium text-muted-foreground mb-2" data-id="element-1186">
                        Sources
                      </div>
                      <div className="bg-muted p-3 rounded border text-xs text-foreground/70 flex justify-between items-center hover:border-red-300 transition-colors cursor-pointer" data-id="element-1187">
                        <span className="font-medium flex items-center" data-id="element-1188">
                          <FileText className="w-3 h-3 mr-2 text-red-700/90" data-id="element-1189" />
                          Defendant Driver ELD Logs.pdf
                        </span>
                        <span className="text-muted-foreground font-mono" data-id="element-1190">
                          RLSF-0119
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </AnimatedSection>
            </div>

            {/* Preview 2: Medical Chronology */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto" data-id="element-1191">
              <AnimatedSection animation="animate-fade-in-left" className="order-2 lg:order-1 relative" data-id="element-1192">
                <div className="absolute -inset-4 bg-orange-50/50 rounded-2xl transform -rotate-2 animate-pulse-glow" data-id="element-1193"></div>
                <Card className="relative shadow-xl border-border bg-card hover:shadow-2xl transition-shadow duration-500" data-id="element-1194">
                  <CardHeader className="border-b bg-muted/50 pb-4" data-id="element-1195">
                    <CardTitle className="text-sm flex items-center text-foreground" data-id="element-1196">
                      <Clock className="w-4 h-4 mr-2 text-orange-500" data-id="element-1197" />
                      Medical Chronology Timeline
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0" data-id="element-1198">
                    <table className="w-full text-xs text-left" data-id="element-1199">
                      <thead className="bg-muted text-muted-foreground border-b" data-id="element-1200">
                        <tr data-id="element-1201">
                          <th className="px-4 py-2 font-medium" data-id="element-1202">Date</th>
                          <th className="px-4 py-2 font-medium" data-id="element-1203">Event</th>
                          <th className="px-4 py-2 font-medium" data-id="element-1204">Source</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y" data-id="element-1205">
                        <tr className="hover:bg-muted/50 transition-colors" data-id="element-1206">
                          <td className="px-4 py-3 font-medium text-foreground" data-id="element-1207">
                            10/12/23
                          </td>
                          <td className="px-4 py-3 text-foreground" data-id="element-1208">
                            ER Visit. Severe L4-L5 pain.
                          </td>
                          <td className="px-4 py-3 text-red-700 font-mono" data-id="element-1209">
                            RLSF-0014
                          </td>
                        </tr>
                        <tr className="bg-orange-50/50 border-l-2 border-l-orange-400" data-id="element-1210">
                          <td className="px-4 py-3 font-medium text-foreground" data-id="element-1211">
                            10/14/23
                          </td>
                          <td className="px-4 py-3 text-foreground" data-id="element-1212">
                            Consultation.{' '}
                            <span className="bg-orange-100 text-orange-800 px-1 rounded inline-block mt-1" data-id="element-1213">
                              Reported prior back pain in 2019.
                            </span>
                          </td>
                          <td className="px-4 py-3 text-red-700 font-mono" data-id="element-1214">
                            RLSF-0056
                          </td>
                        </tr>
                        <tr className="hover:bg-muted/50 transition-colors" data-id="element-1215">
                          <td className="px-4 py-3 font-medium text-foreground" data-id="element-1216">
                            10/18/23
                          </td>
                          <td className="px-4 py-3 text-foreground" data-id="element-1217">
                            MRI. Acute disc herniation.
                          </td>
                          <td className="px-4 py-3 text-red-700 font-mono" data-id="element-1218">
                            RLSF-0074
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </AnimatedSection>
              <AnimatedSection animation="animate-fade-in-right" className="order-1 lg:order-2 space-y-6" data-id="element-1219">
                <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-red-50 text-red-700 border-red-200" data-id="element-1220">
                  Automated Workflows
                </div>
                <h2 className="text-3xl font-bold tracking-tight text-foreground font-heading" data-id="element-1221">
                  Medical chronologies in minutes, not days.
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed" data-id="element-1222">
                  Transform thousands of pages of unstructured medical records
                  from your PI cases into clean, chronological timelines. The
                  system automatically flags ambiguities and pre-existing
                  conditions for your attorneys to review.
                </p>
                <ul className="space-y-3" data-id="element-1223">
                  {['Extracts dates, providers, and treatments', 'Highlights conflicting information', 'Exports directly to Excel/Word'].map((item, i) => <li key={i} className="flex items-center text-sm font-medium text-foreground" data-id="element-1224">
                      <CheckCircle2 className="w-4 h-4 mr-3 text-red-700" data-id="element-1225" />{' '}
                      {item}
                    </li>)}
                </ul>
              </AnimatedSection>
            </div>

            {/* Preview 3: Review Queue & Governance */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto" data-id="element-1226">
              <AnimatedSection animation="animate-fade-in-left" className="space-y-6" data-id="element-1227">
                <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-red-50 text-red-700 border-red-200" data-id="element-1228">
                  Governance & Control
                </div>
                <h2 className="text-3xl font-bold tracking-tight text-foreground font-heading" data-id="element-1229">
                  Attorney-in-the-loop by design.
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed" data-id="element-1230">
                  The platform is built on the principle that AI assists, but
                  attorneys decide. Every automated action passes through a
                  structured review queue, backed by an immutable audit trail.
                </p>
                <ul className="space-y-3" data-id="element-1231">
                  {['Confidence-based flagging system', 'One-click approval or rejection', 'Comprehensive system audit logs'].map((item, i) => <li key={i} className="flex items-center text-sm font-medium text-foreground" data-id="element-1232">
                      <CheckCircle2 className="w-4 h-4 mr-3 text-red-700" data-id="element-1233" />{' '}
                      {item}
                    </li>)}
                </ul>
              </AnimatedSection>
              <AnimatedSection animation="animate-fade-in-right" className="relative" data-id="element-1234">
                <div className="absolute -inset-4 bg-emerald-50/50 rounded-2xl transform rotate-2 animate-pulse-glow" data-id="element-1235"></div>
                <Card className="relative shadow-xl border-border bg-card border-l-4 border-l-amber-500 hover:-translate-y-1 transition-transform duration-300" data-id="element-1236">
                  <CardContent className="p-6" data-id="element-1237">
                    <div className="flex justify-between items-start mb-4" data-id="element-1238">
                      <div data-id="element-1239">
                        <div className="flex items-center space-x-2 mb-1" data-id="element-1240">
                          <Badge variant="outline" className="text-[10px] uppercase tracking-wider" data-id="element-1241">
                            Chronology Conflict
                          </Badge>
                        </div>
                        <h3 className="text-base font-semibold text-foreground" data-id="element-1242">
                          Pre-existing Condition Mention
                        </h3>
                      </div>
                      <Badge variant="warning" className="text-[10px] animate-pulse" data-id="element-1243">
                        Needs Review
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4" data-id="element-1244">
                      Dr. Smith notes mention "prior mild back pain in 2019"
                      which conflicts with intake questionnaire stating no prior
                      back issues.
                    </p>
                    <div className="flex space-x-2" data-id="element-1245">
                      <Button variant="outline" size="sm" className="flex-1 text-destructive hover:text-destructive hover:bg-destructive/10 uppercase tracking-wider text-xs" data-id="element-1246">
                        Reject
                      </Button>
                      <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white uppercase tracking-wider text-xs" data-id="element-1247">
                        Approve
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Roadmap Section */}
        <section id="roadmap" className="py-24 bg-black text-white relative overflow-hidden" data-id="element-1248">
          {/* Background decorative elements */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-red-700/50 to-transparent" data-id="element-1249"></div>
          <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-red-700/10 to-transparent" data-id="element-1250"></div>
          <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-red-700/10 to-transparent" data-id="element-1251"></div>

          <div className="container mx-auto px-4 max-w-5xl relative z-10" data-id="element-1252">
            <AnimatedSection className="text-center mb-16" data-id="element-1253">
              <h2 className="text-3xl font-bold tracking-tight mb-4 font-heading" data-id="element-1254">
                Platform Architecture
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto" data-id="element-1255">
                Designed specifically for Festeryga Law as a matter-centric
                legal workbench with retrieval, evidence, workflows, and
                drafting layered on top.
              </p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-16" data-id="element-1256">
              {[{
              label: 'Frontend',
              value: 'Next.js App Router + TypeScript',
              delay: ''
            }, {
              label: 'Backend API',
              value: 'FastAPI + Pydantic',
              delay: 'delay-100'
            }, {
              label: 'Orchestration',
              value: 'LangGraph (streaming + review)',
              delay: 'delay-200'
            }, {
              label: 'Retrieval',
              value: 'Qdrant hybrid search (dense + sparse)',
              delay: 'delay-300'
            }, {
              label: 'Database',
              value: 'PostgreSQL (matters, docs, audit)',
              delay: 'delay-400'
            }, {
              label: 'Cache / Events',
              value: 'Redis',
              delay: 'delay-500'
            }, {
              label: 'Observability',
              value: 'Langfuse (optional)',
              delay: 'delay-[600ms]'
            }, {
              label: 'Citation Check',
              value: 'CourtListener (optional)',
              delay: 'delay-[700ms]'
            }].map((item, i) => <AnimatedSection key={i} delay={item.delay} data-id="element-1257">
                  <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4 hover:bg-slate-800 transition-colors hover:border-red-700/30 group" data-id="element-1258">
                    <p className="text-xs text-red-400 font-medium uppercase tracking-wider mb-1 group-hover:text-red-300 transition-colors" data-id="element-1259">
                      {item.label}
                    </p>
                    <p className="text-sm text-slate-200 font-medium" data-id="element-1260">
                      {item.value}
                    </p>
                  </div>
                </AnimatedSection>)}
            </div>

            <AnimatedSection delay="delay-200" data-id="element-1261">
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6 mb-24 relative overflow-hidden" data-id="element-1262">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-700 via-emerald-500 to-red-700 opacity-50" data-id="element-1263"></div>
                <h3 className="text-lg font-bold text-white mb-6" data-id="element-1264">
                  Architecture Principles
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1265">
                  <div className="flex items-start group" data-id="element-1266">
                    <div className="w-8 h-8 rounded-full bg-red-900/50 text-red-400 flex items-center justify-center text-sm font-bold mr-4 shrink-0 mt-0.5 group-hover:bg-red-700 group-hover:text-white transition-colors shadow-[0_0_15px_rgba(203,46,46,0.2)]" data-id="element-1267">
                      1
                    </div>
                    <div data-id="element-1268">
                      <h4 className="text-base font-bold text-slate-200 group-hover:text-white transition-colors" data-id="element-1269">
                        Retrieval-first, not chatbot-first
                      </h4>
                      <p className="text-sm text-slate-400 mt-1 leading-relaxed" data-id="element-1270">
                        Built around matter-scoped retrieval, citation-grounded
                        answers, and source previews.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start group" data-id="element-1271">
                    <div className="w-8 h-8 rounded-full bg-red-900/50 text-red-400 flex items-center justify-center text-sm font-bold mr-4 shrink-0 mt-0.5 group-hover:bg-red-700 group-hover:text-white transition-colors shadow-[0_0_15px_rgba(203,46,46,0.2)]" data-id="element-1272">
                      2
                    </div>
                    <div data-id="element-1273">
                      <h4 className="text-base font-bold text-slate-200 group-hover:text-white transition-colors" data-id="element-1274">
                        Workflows are first-class objects
                      </h4>
                      <p className="text-sm text-slate-400 mt-1 leading-relaxed" data-id="element-1275">
                        Modeled as tracked jobs, review tasks, exceptions, and
                        approvals, not side effects.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start group" data-id="element-1276">
                    <div className="w-8 h-8 rounded-full bg-red-900/50 text-red-400 flex items-center justify-center text-sm font-bold mr-4 shrink-0 mt-0.5 group-hover:bg-red-700 group-hover:text-white transition-colors shadow-[0_0_15px_rgba(203,46,46,0.2)]" data-id="element-1277">
                      3
                    </div>
                    <div data-id="element-1278">
                      <h4 className="text-base font-bold text-slate-200 group-hover:text-white transition-colors" data-id="element-1279">
                        Matter isolation at every layer
                      </h4>
                      <p className="text-sm text-slate-400 mt-1 leading-relaxed" data-id="element-1280">
                        Strict isolation in retrieval filters, session scope,
                        and audit records.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start group" data-id="element-1281">
                    <div className="w-8 h-8 rounded-full bg-red-900/50 text-red-400 flex items-center justify-center text-sm font-bold mr-4 shrink-0 mt-0.5 group-hover:bg-red-700 group-hover:text-white transition-colors shadow-[0_0_15px_rgba(203,46,46,0.2)]" data-id="element-1282">
                      4
                    </div>
                    <div data-id="element-1283">
                      <h4 className="text-base font-bold text-slate-200 group-hover:text-white transition-colors" data-id="element-1284">
                        Drafting is downstream of retrieval
                      </h4>
                      <p className="text-sm text-slate-400 mt-1 leading-relaxed" data-id="element-1285">
                        Copilot consumes matter facts, approved sources, and
                        citation checks.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="text-center mb-16" data-id="element-1286">
              <h2 className="text-3xl font-bold tracking-tight mb-4 font-heading" data-id="element-1287">
                Phased Rollout Plan
              </h2>
              <p className="text-slate-400" data-id="element-1288">
                Strategic implementation timeline for Festeryga Law.
              </p>
            </AnimatedSection>

            <div className="space-y-8 relative" data-id="element-1289">
              {/* Vertical timeline line */}
              <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gradient-to-b from-red-700 via-slate-700 to-transparent -translate-x-px" data-id="element-1290"></div>

              <AnimatedSection data-id="element-1291">
                <div className="relative flex items-start gap-6 group" data-id="element-1292">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-slate-900 bg-red-700 text-white shadow-[0_0_15px_rgba(203,46,46,0.5)] shrink-0 z-10" data-id="element-1293">
                    1
                  </div>
                  <div className="flex-1 p-6 rounded-xl border border-red-700/30 bg-slate-800/50 backdrop-blur-sm shadow-lg shadow-red-900/20" data-id="element-1294">
                    <div className="flex items-center justify-between mb-2" data-id="element-1295">
                      <h3 className="font-bold text-lg text-white" data-id="element-1296">
                        Phase 1: Foundation
                      </h3>
                      <Badge variant="secondary" className="bg-red-700/20 text-red-300 border-transparent animate-pulse" data-id="element-1297">
                        Current
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-300" data-id="element-1298">
                      Local node deployment, secure authentication, basic matter
                      management, and document corpus indexing.
                    </p>
                  </div>
                </div>
              </AnimatedSection>

              <AnimatedSection delay="delay-100" data-id="element-1299">
                <div className="relative flex items-start gap-6 group" data-id="element-1300">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-slate-900 bg-slate-700 text-slate-300 shadow shrink-0 z-10 transition-colors group-hover:bg-slate-600" data-id="element-1301">
                    2
                  </div>
                  <div className="flex-1 p-6 rounded-xl border border-slate-800 bg-slate-800/20 transition-colors group-hover:bg-slate-800/40 group-hover:border-slate-700" data-id="element-1302">
                    <div className="flex items-center justify-between mb-2" data-id="element-1303">
                      <h3 className="font-bold text-lg text-slate-300 group-hover:text-white transition-colors" data-id="element-1304">
                        Phase 2: Intelligence
                      </h3>
                      <span className="text-xs text-slate-500 font-medium" data-id="element-1305">
                        Q3 2024
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 group-hover:text-slate-400 transition-colors" data-id="element-1306">
                      Semantic search activation, automated medical chronology
                      generation, and review queue implementation.
                    </p>
                  </div>
                </div>
              </AnimatedSection>

              <AnimatedSection delay="delay-200" data-id="element-1307">
                <div className="relative flex items-start gap-6 group" data-id="element-1308">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-slate-900 bg-slate-700 text-slate-300 shadow shrink-0 z-10 transition-colors group-hover:bg-slate-600" data-id="element-1309">
                    3
                  </div>
                  <div className="flex-1 p-6 rounded-xl border border-slate-800 bg-slate-800/20 transition-colors group-hover:bg-slate-800/40 group-hover:border-slate-700" data-id="element-1310">
                    <div className="flex items-center justify-between mb-2" data-id="element-1311">
                      <h3 className="font-bold text-lg text-slate-300 group-hover:text-white transition-colors" data-id="element-1312">
                        Phase 3: Automation
                      </h3>
                      <span className="text-xs text-slate-500 font-medium" data-id="element-1313">
                        Q4 2024
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 group-hover:text-slate-400 transition-colors" data-id="element-1314">
                      Drafting copilot integration, custom workflow builder, and
                      advanced analytics dashboard.
                    </p>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-card text-center relative overflow-hidden" data-id="element-1315">
          <div className="absolute inset-0 bg-red-50/50 dark:bg-red-950/20" data-id="element-1316" />
          <div className="container mx-auto px-4 relative z-10" data-id="element-1317">
            <AnimatedSection data-id="element-1318">
              <h2 className="text-3xl font-bold tracking-tight text-foreground mb-6 font-heading" data-id="element-1319">
                Ready to see your platform in action?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-xl mx-auto" data-id="element-1320">
                The demo environment is populated with sample data for the
                "Ramirez v. Lone Star Freight" matter — a realistic plaintiff
                trucking injury case.
              </p>
              <Button size="lg" className="bg-red-700 hover:bg-red-600 text-white font-semibold h-14 px-10 shadow-lg shadow-red-700/25 hover:shadow-xl hover:shadow-red-700/35 hover:-translate-y-1 transition-all duration-300 uppercase tracking-wider" onClick={() => onNavigate('login')} data-id="element-1321">
                Launch Demo Environment <ArrowRight className="ml-2 w-5 h-5" data-id="element-1322" />
              </Button>
            </AnimatedSection>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 bg-muted" data-id="element-1323">
        <div className="container mx-auto px-4 flex flex-col md:flex-row justify-between items-center text-sm text-muted-foreground" data-id="element-1324">
          <div className="flex items-center space-x-2 mb-4 md:mb-0" data-id="element-1325">
            <img src="https://cdn.magicpatterns.com/uploads/xaM7Qq1sxDCqJoWJci6P4r/EF-logo-white.png" alt="Festeryga Law" className="h-5 w-auto invert" data-id="element-1326" />
          </div>
          <div className="flex flex-col items-center md:items-end gap-1" data-id="element-1327">
            <p data-id="element-1328">Internal Prototype v0.9.0 • Confidential</p>
            <p className="text-xs text-muted-foreground/70" data-id="element-1329">
              © {new Date().getFullYear()} All rights reserved. Platform
              designed & developed by{' '}
              <a href="https://pendoah.ai" target="_blank" rel="noopener noreferrer" className="text-red-700 hover:text-red-600 font-medium transition-colors" data-id="element-1330">
                Pendoah.ai
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>;
}